import React from 'react';
import ReactDOM from 'react-dom/client';
import { init, miniApp, viewport, backButton, closingBehavior, swipeBehavior, retrieveLaunchParams } from '@telegram-apps/sdk-react';
import { App } from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { TelegramRequiredScreen } from './components/TelegramRequiredScreen';
import { tapApi } from './services/tapApi';
import { initGTM, setGTMUserId } from './lib/gtm';
import './index.css';

// Mock Telegram environment in development mode (must be before init())
if (import.meta.env.DEV) {
  await import('./mockEnv');
}

// Initialize Telegram SDK
async function initializeApp() {
  try {
    // Initialize SDK
    init();

    // Mount SDK components - wrap in try-catch for non-TG environments
    try {
      miniApp.mount();
    } catch (err) {
      console.warn('[TG SDK] miniApp.mount failed:', err);
    }
    try {
      viewport.mount();
    } catch (err) {
      console.warn('[TG SDK] viewport.mount failed:', err);
    }
    try {
      backButton.mount();
    } catch (err) {
      console.warn('[TG SDK] backButton.mount failed:', err);
    }
    try {
      closingBehavior.mount();
    } catch (err) {
      console.warn('[TG SDK] closingBehavior.mount failed:', err);
    }
    try {
      swipeBehavior.mount();
    } catch {
      // swipeBehavior requires Mini Apps version 7.7+
    }

    // Expand viewport
    try {
      if (!viewport.isExpanded()) {
        viewport.expand();
      }
    } catch {
      // Viewport not available
    }

    // Disable vertical swipes to prevent accidental close during tap game
    try {
      swipeBehavior.disableVertical();
    } catch {
      // Swipe behavior not available
    }

    // Set mini app ready
    try {
      miniApp.ready();
    } catch {
      // Mini app not available
    }

    // Force dark theme colors
    const darkColor = '#05050a';
    try {
      miniApp.setBackgroundColor(darkColor);
      miniApp.setHeaderColor(darkColor);
    } catch {
      // Fallback for older SDK versions
      try {
        window.Telegram?.WebApp?.setBackgroundColor?.(darkColor);
        window.Telegram?.WebApp?.setHeaderColor?.(darkColor);
      } catch {
        // Not available
      }
    }

    // Get raw launch params for API auth using SDK
    try {
      const { initDataRaw } = retrieveLaunchParams();
      console.log('[TG] initDataRaw from SDK:', initDataRaw?.substring(0, 50));
      console.log('[TG] initDataRaw length:', initDataRaw?.length);
      if (initDataRaw) {
        tapApi.setInitData(initDataRaw);
        console.log('[TG] initData set successfully');
      } else {
        console.warn('[TG] No initDataRaw from SDK!');
        // Fallback to window.Telegram.WebApp
        const webApp = window.Telegram?.WebApp;
        if (webApp?.initData) {
          tapApi.setInitData(webApp.initData);
          console.log('[TG] initData set from window.Telegram fallback');
        }
      }
    } catch (err) {
      console.error('[TG] Error getting launch params:', err);
    }
  } catch (err) {
    console.error('Failed to initialize Telegram SDK:', err);
  }
}

// Initialize and render
initializeApp().then(() => {
  // Check if we have initData from SDK or fallback
  const hasInitData = Boolean(tapApi.getInitData());
  
  // Debug info for development
  const debugInfo = {
    hasInitData,
    initDataLength: tapApi.getInitData()?.length || 0,
    hasTelegramWindow: Boolean(window.Telegram?.WebApp),
  };
  console.log('[TG] Debug info:', debugInfo);

  // Initialize Google Tag Manager (no-ops when VITE_GTM_ID is empty)
  initGTM(import.meta.env.VITE_GTM_ID);

  // Pass Telegram user ID for GA4 user identification (replaces cookie-based tracking)
  try {
    const tgUserId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id;
    if (tgUserId) {
      setGTMUserId(tgUserId);
    }
  } catch {
    // User ID not available
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <ErrorBoundary>
        {hasInitData ? <App /> : <TelegramRequiredScreen />}
      </ErrorBoundary>
    </React.StrictMode>
  );
});

// Type declaration for Telegram WebApp
declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData?: string;
        initDataUnsafe: {
          user?: {
            id: number;
            first_name: string;
            last_name?: string;
            username?: string;
            language_code?: string;
            is_premium?: boolean;
          };
          start_param?: string;
        };
        ready: () => void;
        expand: () => void;
        close: () => void;
        openLink: (url: string) => void;
        openTelegramLink: (url: string) => void;
        setBackgroundColor?: (color: string) => void;
        setHeaderColor?: (color: string) => void;
        HapticFeedback: {
          impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
          notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
        };
      };
    };
  }
}
